import React, { useEffect, useState } from "react";
import axios from "axios";
import "./App.css";

// UI PREVIEW MODE - Set to false for production
const USE_MOCK_DATA = false;

const MOCK_DATA = {
  nav: { NAV: 982500.50, available_funds: 125000.50, equities: 875000.00, margin_balance: -15000.00 },
  positions: [
    { description: "NVIDIA Corporation", symbol: "NVDA", market_value: 245000, quantity: 175 },
    { description: "Apple Inc.", symbol: "AAPL", market_value: 180000, quantity: 800 },
    { description: "Microsoft Corporation", symbol: "MSFT", market_value: 165000, quantity: 400 },
    { description: "Amazon.com Inc.", symbol: "AMZN", market_value: 145000, quantity: 750 },
    { description: "Alphabet Inc.", symbol: "GOOGL", market_value: 140000, quantity: 800 },
  ],
  fees: { management_fee: 12500, gp_fee: 45000 }
};

const Dashboard = () => {
  const [navData, setNavData] = useState({
    NAV: 0,
    available_funds: 0,
    equities: 0,
    margin_balance: 0,
  });
  const [positions, setPositions] = useState([]);
  const [fees, setFees] = useState({ management_fee: 0, gp_fee: 0 });
  const [schwabError, setSchwabError] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = "https://fundtracker.reicheltcapital.com/api";

  useEffect(() => {
    const fetchSchwabData = async () => {
      // Use mock data for UI preview
      if (USE_MOCK_DATA) {
        setTimeout(() => {
          setNavData(MOCK_DATA.nav);
          setPositions(MOCK_DATA.positions);
          setFees(MOCK_DATA.fees);
          setLoading(false);
        }, 500);
        return;
      }

      const queryParams = new URLSearchParams(window.location.search);
      const token = queryParams.get("token") || sessionStorage.getItem("authToken");

      if (!token) {
        setSchwabError("No authentication token found. Please log in.");
        setLoading(false);
        return;
      }

      sessionStorage.setItem("authToken", token);
      const headers = { Authorization: `Bearer ${token}` };

      try {
        setLoading(true);
        const [navResponse, balancesResponse, positionsResponse, feesResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/compute-nav`, { headers }),
          axios.get(`${API_BASE_URL}/get-balances`, { headers }),
          axios.get(`${API_BASE_URL}/get-positions`, { headers }),
          axios.get(`${API_BASE_URL}/compute-fees`, { headers }),
        ]);

        setNavData({
          NAV: navResponse.data.NAV || 0,
          available_funds: navResponse.data.available_funds || 0,
          equities: navResponse.data.equities || 0,
          margin_balance: navResponse.data.margin_balance || 0,
        });
        setPositions(positionsResponse.data.positions || []);
        setFees({
          management_fee: feesResponse.data.management_fee || 0,
          gp_fee: feesResponse.data.gp_fee || 0,
        });
        setSchwabError(positionsResponse.data.error || null);
      } catch (error) {
        const errorMsg = error.response?.data?.detail || error.message || "Unknown error fetching Schwab data";
        setSchwabError(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    fetchSchwabData();
  }, []);

  const formatCurrency = (num) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);

  const formatNumber = (num) =>
    num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  if (loading) {
    return (
      <div className="page-container">
        <h1>Fund Tracker Dashboard</h1>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <div className="status-badge loading">
            <span className="spinner"></span>
            Loading dashboard data...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1>Fund Tracker Dashboard</h1>

      {schwabError && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
          <div className="status-badge error">{schwabError}</div>
        </div>
      )}

      <div className="stats-grid">
        {/* Portfolio Summary Card */}
        <div className="glass-card">
          <h3>Portfolio Summary</h3>
          <div className="stat-item">
            <span className="stat-label">Total NAV</span>
            <span className="stat-value">{formatCurrency(navData.NAV)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Available Funds</span>
            <span className="stat-value">{formatCurrency(navData.available_funds)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Position Value</span>
            <span className="stat-value">{formatCurrency(navData.equities)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Margin Balance</span>
            <span className={`stat-value ${navData.margin_balance < 0 ? 'negative' : ''}`}>
              {formatCurrency(navData.margin_balance)}
            </span>
          </div>
        </div>

        {/* Fees Card */}
        <div className="glass-card">
          <h3>Estimated Year-End Fees</h3>
          <div className="stat-item">
            <span className="stat-label">Management Fee (2%)</span>
            <span className="stat-value negative">{formatCurrency(fees.management_fee)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">GP Performance Fee</span>
            <span className="stat-value negative">{formatCurrency(fees.gp_fee)}</span>
          </div>
          <div className="stat-item" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '16px', marginTop: '8px' }}>
            <span className="stat-label" style={{ fontWeight: '600', color: '#fff' }}>Total Fees</span>
            <span className="stat-value negative">{formatCurrency(fees.management_fee + fees.gp_fee)}</span>
          </div>
        </div>

        {/* Positions Card */}
        <div className="glass-card" style={{ gridColumn: 'span 2' }}>
          <h3>Current Positions</h3>
          {positions.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="glass-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Description</th>
                    <th style={{ textAlign: 'right' }}>Shares</th>
                    <th style={{ textAlign: 'right' }}>Market Value</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((position, index) => (
                    <tr key={index}>
                      <td style={{ fontWeight: '600', color: '#4ecdc4' }}>{position.symbol}</td>
                      <td>{position.description}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                        {formatNumber(position.quantity)}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: '500' }}>
                        {formatCurrency(position.market_value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <td colSpan="3" style={{ fontWeight: '600' }}>Total Position Value</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: '600', color: '#fff' }}>
                      {formatCurrency(positions.reduce((sum, p) => sum + p.market_value, 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>
              No positions available
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
